import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  TableSortLabel,
  TextField,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatPhoneNumber } from "../../utils/FormatPhone";
import { Delete } from "@mui/icons-material";

const getStatusColor = (deletedAt, emailConfirmed) => {
  if (deletedAt) return "bg-red-500";
  return emailConfirmed ? "bg-green-500" : "bg-orange-500";
};

const formatDate = (date) =>
  format(new Date(date), "dd/MM/yyyy", { locale: ptBR });

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const EmployeesTable = ({ clients, onEdit, onDelete }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  // Filtros
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      if (filterStatus === "deleted" && !client.deletedAt) return false;
      if (
        filterStatus === "confirmed" &&
        (!client.emailConfirmed || client.deletedAt)
      )
        return false;
      if (
        filterStatus === "unconfirmed" &&
        (client.emailConfirmed || client.deletedAt)
      )
        return false;

      return (
        client.name.toLowerCase().includes(filterName.toLowerCase()) &&
        client.email.toLowerCase().includes(filterEmail.toLowerCase())
      );
    });
  }, [clients, filterName, filterEmail, filterStatus]);

  const sortedClients = useMemo(() => {
    return [...filteredClients].sort(getComparator(order, orderBy));
  }, [filteredClients, order, orderBy]);

  const paginatedClients = useMemo(() => {
    return sortedClients.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedClients, page, rowsPerPage]);

  return (
    <div className="flex flex-col">
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <TextField
          label="Filtrar por Nome"
          variant="outlined"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          size="small"
        />
        <TextField
          label="Filtrar por Email"
          variant="outlined"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
        />
        <TextField
          select
          // label="Filtrar por Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          SelectProps={{ native: true }}
          size="small"
        >
          <option value="">Todos</option>
          <option value="deleted">Deletado</option>
          <option value="confirmed">Confirmado</option>
          <option value="unconfirmed">Não Confirmado</option>
        </TextField>
        {/* <TextField
          label="Filtrar por Cidade"
          variant="outlined"
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
          size="small"
        /> */}
        {/* <TextField
          label="Filtrar por Vendedor"
          variant="outlined"
          value={filterSeller}
          onChange={(e) => setFilterSeller(e.target.value)}
          size="small"
        /> */}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Nome
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === "email" ? order : false}>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => handleRequestSort("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Contato</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Cargo</TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "createdAt" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Data de Cadastro
                </TableSortLabel>
              </TableCell>
              {/* <TableCell align="center">Cidade</TableCell> */}
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedClients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.name}</TableCell>
                <TableCell>{client.email}</TableCell>
                <TableCell align="center">
                  {formatPhoneNumber(client.phone)}
                </TableCell>
                <TableCell align="center">
                  <div className="flex justify-center">
                    <div
                      className={`w-3 h-3 rounded-full ${getStatusColor(
                        client.deletedAt,
                        client.emailConfirmed
                      )}`}
                    ></div>
                  </div>
                </TableCell>
                <TableCell align="center">{client.role}</TableCell>
                <TableCell align="center">
                  {formatDate(client.createdAt)}
                </TableCell>
                {/* <TableCell
                  align="center"
                  className="whitespace-normal break-words"
                  width={200}
                >
                  {client.billingAddress
                    ? `${client.billingAddress.city}/${client.billingAddress.state}`
                    : "Sem Endereço Cadastrado"}
                </TableCell> */}
                <TableCell align="center">
                  <IconButton onClick={() => onEdit(client)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      if (
                        window.confirm(
                          "Deseja realmente deletar este colaborador?"
                        )
                      ) {
                        onDelete(client.id);
                      }
                    }}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
        />
      </TableContainer>
    </div>
  );
};

export default EmployeesTable;
